/**
 * returns true if the first date is after the second one
 */
export function isAfter(date1: Date, date2: Date): boolean {
  return date1 > date2;
}

const NUM_MILLISECONDS_IN_SECOND = 1000;
const NUM_SECONDS_IN_MINUTE = 60;
const NUM_MINUTES_IN_HOUR = 60;
const NUM_SECONDS_IN_HOUR = NUM_SECONDS_IN_MINUTE * NUM_MINUTES_IN_HOUR;
const NUM_HOURS_IN_DAY = 24;
const NUM_MILLISECONDS_IN_DAY =
  NUM_HOURS_IN_DAY * NUM_SECONDS_IN_HOUR * NUM_MILLISECONDS_IN_SECOND;

export function secondsToHoursExact(seconds: number): number {
  return seconds / NUM_SECONDS_IN_HOUR;
}

export function secondsToHours(seconds: number): number {
  return Math.floor(secondsToHoursExact(seconds));
}

// in local time
export function getLocalYear(date: Date): number {
  return date.getFullYear();
}

// in UTC time
export function getUTCYear(date: Date): number {
  return date.getUTCFullYear();
}

export function startOfDayLocal(date: Date): Date {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
}

export function startOfDayUTC(date: Date): Date {
  const newDate = new Date(date);
  newDate.setUTCHours(0, 0, 0, 0);
  return newDate;
}

/**
 * Returns the number of 24 hour cycles between the two dates. aka the number of days in UTC between
 * the two dates.
 *
 * NOTE: This differs from date-fns which doesn't count the number of 24 hour periods, but rather
 * the number of cycles to the "same local time next day" which may be more or less than 24 hours
 * with daylight savings.
 */
export function differenceInDays(date1: Date, date2: Date): number {
  return Math.trunc(
    (date1.getTime() - date2.getTime()) / NUM_MILLISECONDS_IN_DAY
  ); // trunc to ensure it'll be a whole number
}

// Get the number of calendar days between the given dates in local time. This means that the times are removed from the dates and then the difference in days is calculated.
export function differenceInCalendarDaysLocal(
  date1: Date,
  date2: Date
): number {
  const date1WithoutTime = startOfDayLocal(date1);
  const date2WithoutTime = startOfDayLocal(date2);
  return differenceInDays(date1WithoutTime, date2WithoutTime);
}

// Get the number of calendar days between the given dates in UTC. This means that the times are removed from the dates and then the difference in days is calculated.
export function differenceInCalendarDaysUTC(date1: Date, date2: Date): number {
  const date1WithoutTime = startOfDayUTC(date1);
  const date2WithoutTime = startOfDayUTC(date2);
  return differenceInDays(date1WithoutTime, date2WithoutTime);
}

// can manually add a new format as necessary. Each one maps to a valid date-fns token string
export enum DateFormat {
  MONTH = 'MMM',
  MONTH_DAY_YEAR = 'MMM dd, yyyy',
  MONTH_YEAR = 'MMM yyyy',
  MONTH_DAY = 'MMM dd',
  DAY = 'dd',
  YEAR = 'yyyy',
}

export function format(
  date: Date,
  dateFormat: DateFormat,
  timeZone?: string
): string {
  switch (dateFormat) {
    case DateFormat.MONTH: {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        timeZone,
      }).format(date);
    }
    case DateFormat.MONTH_DAY_YEAR: {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        timeZone,
      }).format(date);
    }
    case DateFormat.MONTH_YEAR: {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        year: 'numeric',
        timeZone,
      }).format(date);
    }
    case DateFormat.MONTH_DAY: {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: '2-digit',
        timeZone,
      }).format(date);
    }
    case DateFormat.DAY: {
      return new Intl.DateTimeFormat('en-US', {
        day: '2-digit',
        timeZone,
      }).format(date);
    }
    case DateFormat.YEAR: {
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        timeZone,
      }).format(date);
    }
  }
}

export function addYearsUTC(date: Date, amount: number) {
  const newDate = new Date(date);
  newDate.setUTCFullYear(getUTCYear(date) + amount);
  return newDate;
}

export function subYearsUTC(date: Date, amount: number) {
  return addYearsUTC(date, -amount);
}

export function addYearsLocal(date: Date, amount: number) {
  const newDate = new Date(date);
  newDate.setFullYear(getLocalYear(date) + amount);
  return newDate;
}

export function subYearsLocal(date: Date, amount: number) {
  return addYearsLocal(date, -amount);
}

export function compareAsc(date1: Date, date2: Date): number {
  return Math.sign(date1.getTime() - date2.getTime()); // returns -1, 0, or 1 for sorting
}

export function max(dates: Date[]): Date | null {
  if (dates.length === 0) {
    return null;
  }
  return dates.reduce((maxDate, date2) => (maxDate > date2 ? maxDate : date2));
}

export function addDaysLocal(date: Date, amount: number): Date {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + amount);
  return newDate;
}

export function addDaysUTC(date: Date, amount: number): Date {
  const newDate = new Date(date);
  newDate.setUTCDate(newDate.getUTCDate() + amount);
  return newDate;
}

export function subDaysLocal(date: Date, amount: number): Date {
  return addDaysLocal(date, -amount);
}

export function subDaysUTC(date: Date, amount: number): Date {
  return addDaysUTC(date, -amount);
}

export function addMonthsUTC(date: Date, amount: number) {
  const newDate = new Date(date);
  newDate.setUTCMonth(newDate.getUTCMonth() + amount);
  return newDate;
}

export function addMonthsLocal(date: Date, amount: number) {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() + amount);
  return newDate;
}

export function subMonthsUTC(date: Date, amount: number) {
  return addMonthsUTC(date, -amount);
}

export function subMonthsLocal(date: Date, amount: number) {
  return addMonthsLocal(date, -amount);
}

export function isBefore(date1: Date, date2: Date) {
  return date1 < date2;
}
